import React, { useEffect, useState } from "react";
import { api, fmtINR } from "../lib/api";
import { Plus, Trash2, Warehouse, Phone } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [outstanding, setOutstanding] = useState({});
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");

  const load = async () => {
    const [sRes, pRes] = await Promise.all([
      api.get("/suppliers"),
      api.get("/reports/payables"),
    ]);
    setSuppliers(sRes.data);
    const map = {};
    pRes.data.suppliers.forEach((s) => (map[s.supplier] = s.outstanding));
    setOutstanding(map);
  };

  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!name.trim()) {
      toast.error("Enter supplier name");
      return;
    }
    await api.post("/suppliers", { name: name.trim(), phone, address, notes });
    toast.success("Supplier added");
    setName("");
    setPhone("");
    setAddress("");
    setNotes("");
    load();
  };

  const del = async (s) => {
    if (!window.confirm(`Delete supplier "${s.name}"?`)) return;
    await api.delete(`/suppliers/${s.id}`);
    load();
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto page-enter" data-testid="suppliers-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1">
          <Warehouse className="h-3 w-3" /> Vendors
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-1">
          Suppliers
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="border border-zinc-200 bg-white p-5">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3">Add Supplier</div>
            <input
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="supplier-name-input"
              className="w-full border border-zinc-300 px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-sky-600"
            />
            <input
              placeholder="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="supplier-phone-input"
              className="w-full border border-zinc-300 px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-sky-600"
            />
            <input
              placeholder="Address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              data-testid="supplier-address-input"
              className="w-full border border-zinc-300 px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-sky-600"
            />
            <input
              placeholder="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="supplier-notes-input"
              className="w-full border border-zinc-300 px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-sky-600"
            />
            <button
              onClick={add}
              data-testid="supplier-add-btn"
              className="w-full bg-sky-600 text-white px-4 py-2 text-sm uppercase tracking-wider hover:bg-sky-700 flex items-center justify-center gap-1"
            >
              <Plus className="h-4 w-4" /> Add Supplier
            </button>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="border border-zinc-200 bg-white" data-testid="suppliers-list">
            {suppliers.length === 0 ? (
              <div className="p-6 text-center text-sm text-zinc-500">
                No suppliers yet — add your regular vendors here
              </div>
            ) : (
              suppliers.map((s) => (
                <div
                  key={s.id}
                  className="border-b border-zinc-200 px-4 py-3 flex items-center justify-between gap-3"
                  data-testid={`supplier-item-${s.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">{s.name}</div>
                    <div className="text-xs text-zinc-500 flex items-center gap-3 mt-0.5">
                      {s.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" /> {s.phone}
                        </span>
                      )}
                      {s.address && <span className="truncate">{s.address}</span>}
                      {s.notes && <span className="italic truncate">{s.notes}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {(outstanding[s.name] || 0) > 0 ? (
                      <Link
                        to="/payables"
                        className="text-right hover:underline"
                        data-testid={`supplier-outstanding-${s.id}`}
                      >
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">Owed</div>
                        <div className="font-mono-num font-bold text-sm text-rose-600">
                          {fmtINR(outstanding[s.name])}
                        </div>
                      </Link>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider text-emerald-600">Clear</span>
                    )}
                    <button
                      onClick={() => del(s)}
                      className="text-zinc-300 hover:text-rose-600"
                      data-testid={`supplier-delete-${s.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
